import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { adminTokens as T } from '../../../theme/adminTokens';

export interface KPIStatCardProps {
  label: string;
  value: number | string | null | undefined;
  delta?: number | null;
  deltaLabel?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info';
  icon?: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
  format?: (v: number) => string;
}

const toneToColors = (tone: KPIStatCardProps['tone']) => {
  switch (tone) {
    case 'success': return { bg: T.color.success.soft, text: T.color.success.text };
    case 'warning': return { bg: T.color.warning.soft, text: T.color.warning.text };
    case 'error':   return { bg: T.color.error.soft,   text: T.color.error.text };
    case 'info':    return { bg: T.color.info.soft,    text: T.color.info.text };
    default:        return { bg: T.color.brand[50],    text: T.color.brand[600] };
  }
};

const KPIStatCard: React.FC<KPIStatCardProps> = ({
  label, value, delta, deltaLabel = 'vs. last period', tone = 'neutral',
  icon, loading, onClick, format,
}) => {
  const iconColors = toneToColors(tone);
  const displayValue =
    loading ? '—' :
    value == null ? '—' :
    typeof value === 'number'
      ? (format ? format(value) : value.toLocaleString())
      : value;

  const deltaIsUp = typeof delta === 'number' && delta > 0;
  const deltaIsDown = typeof delta === 'number' && delta < 0;
  const deltaColor = deltaIsUp ? T.color.success.text : deltaIsDown ? T.color.error.text : T.color.text.tertiary;
  const DeltaIcon = deltaIsUp ? TrendingUp : deltaIsDown ? TrendingDown : TrendingFlat;

  return (
    <Box
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        position: 'relative',
        bgcolor: T.color.surface.raised,
        border: `1px solid ${T.color.surface.border}`,
        borderRadius: `${T.radius.lg}px`,
        p: `${T.space[5]}px`,
        boxShadow: T.shadow.sm,
        cursor: onClick ? 'pointer' : 'default',
        transition: `box-shadow ${T.motion.fast}, transform ${T.motion.fast}, border-color ${T.motion.fast}`,
        outline: 'none',
        '&:hover': onClick ? { boxShadow: T.shadow.md, transform: 'translateY(-1px)', borderColor: T.color.surface.borderStrong } : {},
        '&:focus-visible': { boxShadow: `0 0 0 3px ${T.color.brand[200]}` },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: `${T.space[3]}px` }}>
        <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.medium, color: T.color.text.secondary, letterSpacing: 0.2 }}>
          {label}
        </Typography>
        {icon && (
          <Box sx={{
            width: 32, height: 32, borderRadius: `${T.radius.md}px`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: iconColors.bg, color: iconColors.text,
            '& > svg': { fontSize: 18 },
          }}>
            {icon}
          </Box>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="text" width="60%" height={42} />
      ) : (
        <Typography sx={{
          fontSize: T.type.size['2xl'],
          fontWeight: T.type.weight.bold,
          color: T.color.text.primary,
          lineHeight: T.type.lineHeight.tight,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {displayValue}
        </Typography>
      )}

      {typeof delta === 'number' && !loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[1]}px`, mt: `${T.space[2]}px` }}>
          <DeltaIcon sx={{ fontSize: 16, color: deltaColor }} />
          <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: deltaColor, fontVariantNumeric: 'tabular-nums' }}>
            {deltaIsUp ? '+' : ''}{delta}%
          </Typography>
          <Typography sx={{ fontSize: T.type.size.sm, color: T.color.text.tertiary }}>
            {deltaLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KPIStatCard;
